import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";
import { formatOrdinalDateTime } from "./clientsTable";

export const CUSTOMERS_TABLE_LIMIT = 25;

function str(v: unknown): string {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

export type CustomerRecord = Record<string, unknown> & {
  _id?: string;
  profile?: { firstName?: string; lastName?: string };
  email?: { address?: string; isVerified?: boolean };
  phone?: { number?: string };
  kyc?: { status?: string };
  createdAt?: string;
  lastLogin?: string;
};

export type CustomerRow = BaseRowItem & {
  raw: CustomerRecord;
  name: string;
  email: string;
  kycLevel: string;
  phone: string;
  dateCreated: string;
  lastLogin: string;
};

export function customerKycLevel(customer: CustomerRecord): string {
  const verified = customer.email?.isVerified;
  if (verified && customer.kyc?.status === "VERIFIED") return "Level 2";
  if (verified) return "Level 1";
  return "None";
}

export function buildCustomerRows(customers: CustomerRecord[]): CustomerRow[] {
  return customers.map((customer, index) => {
    const first = str(customer.profile?.firstName);
    const last = str(customer.profile?.lastName);
    return {
      id: str(customer._id) || `customer-${index}`,
      raw: customer,
      name: `${first || "-----"} ${last || "-----"}`.trim(),
      email: str(customer.email?.address) || "-----",
      kycLevel: customerKycLevel(customer),
      phone: str(customer.phone?.number) || "Not Added",
      dateCreated: formatOrdinalDateTime(customer.createdAt),
      lastLogin: formatOrdinalDateTime(customer.lastLogin),
    };
  });
}

export function createCustomerColumns(
  onOpen: (row: CustomerRow) => void
): ColumnDef<CustomerRow, unknown>[] {
  return [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ getValue }) => (
        <span className="capitalize text-[14px] text-white">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      meta: { hideBelow: "sm" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#AEBCC6]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "kycLevel",
      header: "KYC",
      cell: ({ getValue }) => (
        <span className="inline-flex rounded-full bg-[#3A4852] px-3 py-1 text-[11px] font-bold text-[#60B7F6]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "phone",
      header: "Phone number",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#E7EBEE]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "dateCreated",
      header: "Date Created",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-[#869AA9]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "lastLogin",
      header: "Last Login",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-[#869AA9]">{String(getValue())}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button transition hover:border-secondary_button hover:bg-[#273037]"
          onClick={() => onOpen(row.original)}
        >
          View
        </button>
      ),
    },
  ];
}

function csvCell(value: unknown): string {
  const s = value == null ? "" : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildCustomersCsv(customers: CustomerRecord[]): string {
  const header = [
    "Name",
    "Email",
    "Contact",
    "KYC",
    "Date created",
    "Last login",
  ];
  const rows = customers.map((c) => {
    const first = str(c.profile?.firstName);
    const last = str(c.profile?.lastName);
    return [
      csvCell(`${first} ${last}`.trim()),
      csvCell(str(c.email?.address)),
      csvCell(str(c.phone?.number) || "Not added"),
      csvCell(str(c.kyc?.status)),
      csvCell(formatOrdinalDateTime(c.createdAt)),
      csvCell(formatOrdinalDateTime(c.lastLogin)),
    ];
  });
  return [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
}
